import {
  AnonymousChatRequest,
  AnonymousChatResponse,
  Chat,
  ChatListResponse,
  ChatWithMessages,
  SendMessageRequest,
  SendMessageResponse,
  StartChatRequest,
  StartChatResponse,
  StreamCallbacks,
  StreamDoneEvent,
  StreamStartEvent,
  StreamTitleEvent,
} from "../types/chat";
import { HealthThreatData } from "../types/health-threat";
import { MedicalSummaryData } from "../types/medical-summary";
import { API_BASE_URL, apiClient } from "./api";

export class ChatService {
  /**
   * 새로운 채팅을 시작합니다
   */
  static async startChat(
    request: StartChatRequest
  ): Promise<StartChatResponse> {
    const response = await apiClient.post<StartChatResponse>(
      "/chat/start",
      request
    );
    return response.data;
  }

  /**
   * 사용자의 모든 채팅 목록을 조회합니다
   */
  static async getChatList(): Promise<Chat[]> {
    const response = await apiClient.get<ChatListResponse>("/chat");
    return response.data.chats;
  }

  /**
   * 특정 채팅의 상세 정보와 메시지를 조회합니다
   */
  static async getChat(chatId: string): Promise<ChatWithMessages> {
    const response = await apiClient.get<ChatWithMessages>(`/chat/${chatId}`);
    return response.data;
  }

  /**
   * 기존 채팅에 메시지를 전송합니다
   */
  static async sendMessage(
    request: SendMessageRequest
  ): Promise<SendMessageResponse> {
    const response = await apiClient.post<SendMessageResponse>(
      "/chat/message",
      request
    );
    return response.data;
  }

  /**
   * 채팅을 삭제합니다
   */
  static async deleteChat(chatId: string): Promise<void> {
    await apiClient.delete(`/chat/${chatId}`);
  }

  /**
   * 익명 사용자의 채팅을 처리합니다
   */
  static async sendAnonymousMessage(
    request: AnonymousChatRequest
  ): Promise<AnonymousChatResponse> {
    const response = await apiClient.post<AnonymousChatResponse>(
      "/chat/anonymous",
      request
    );
    return response.data;
  }

  /**
   * 문진요약서를 생성합니다
   */
  static async getMedicalSummary(
    chatId?: string
  ): Promise<MedicalSummaryData> {
    const response = await apiClient.post<MedicalSummaryData>(
      "/chat/medical-summary",
      { chatId }
    );
    return response.data;
  }

  /**
   * 건강 위험 분석을 요청합니다
   */
  static async getHealthThreat(): Promise<HealthThreatData> {
    const response = await apiClient.post<HealthThreatData>(
      "/chat/health-threat"
    );
    return response.data;
  }

  // ============================================================
  // SSE 스트리밍 메서드
  // ============================================================

  /**
   * SSE 스트림을 파싱하는 내부 헬퍼
   * 하나의 reader.read() 청크에 여러 token 이벤트가 포함될 수 있으므로,
   * 토큰 사이에 비동기 yield를 넣어 React가 각 토큰을 별도로 렌더링하도록 함
   */
  private static async parseSSEStream(
    response: Response,
    callbacks: StreamCallbacks
  ): Promise<void> {
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // 이벤트 단위로 파싱 (\n\n 로 구분)
        const events = buffer.split("\n\n");
        buffer = events.pop()!; // 마지막 불완전한 이벤트는 버퍼에 유지

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          const lines = eventBlock.split("\n");
          let eventType = "";
          let eventData = "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7);
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const parsed = JSON.parse(eventData);

            switch (eventType) {
              case "start":
                callbacks.onStart?.(parsed as StreamStartEvent);
                break;
              case "token": {
                const text = parsed.token as string;
                // 원본 청크를 그대로 전달 (타이핑 애니메이션은 UI 레이어에서 처리)
                callbacks.onToken(text);
                // React 배칭 방지를 위한 최소 딜레이
                await new Promise<void>((r) => setTimeout(r, 0));
                break;
              }
              case "title":
                callbacks.onTitle?.(parsed as StreamTitleEvent);
                break;
              case "done":
                callbacks.onDone(parsed as StreamDoneEvent);
                break;
              case "error":
                callbacks.onError(parsed.message || "알 수 없는 오류");
                break;
            }
          } catch {
            // JSON 파싱 실패 시 무시
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * SSE fetch 요청 생성 헬퍼
   */
  private static async fetchSSE(
    endpoint: string,
    body: unknown,
    signal?: AbortSignal
  ): Promise<Response> {
    const accessToken = localStorage.getItem("accessToken");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP ${response.status} 오류가 발생했습니다.`
      );
    }

    return response;
  }

  /**
   * 기존 채팅에 메시지를 전송합니다 (SSE 스트리밍)
   */
  static async sendMessageStream(
    request: SendMessageRequest,
    callbacks: StreamCallbacks,
    signal?: AbortSignal
  ): Promise<void> {
    const response = await this.fetchSSE(
      "/chat/message/stream",
      request,
      signal
    );
    await this.parseSSEStream(response, callbacks);
  }

  /**
   * 새로운 채팅을 시작합니다 (SSE 스트리밍)
   */
  static async startChatStream(
    request: StartChatRequest,
    callbacks: StreamCallbacks,
    signal?: AbortSignal
  ): Promise<void> {
    const response = await this.fetchSSE(
      "/chat/start/stream",
      request,
      signal
    );
    await this.parseSSEStream(response, callbacks);
  }

  /**
   * 익명 사용자의 채팅을 처리합니다 (SSE 스트리밍)
   */
  static async sendAnonymousMessageStream(
    request: AnonymousChatRequest,
    callbacks: StreamCallbacks,
    signal?: AbortSignal
  ): Promise<void> {
    const response = await this.fetchSSE(
      "/chat/anonymous/stream",
      request,
      signal
    );
    await this.parseSSEStream(response, callbacks);
  }
}
